import React, { useState } from "react";
import { Card, Input, Field } from "@/components/UI";
import { CalendarBlank, CaretLeft, CaretRight, Lock, PencilSimple } from "@phosphor-icons/react";

const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

// Akordiyon aç/kapa — tıklanan kartın ekrandaki konumunu korur (sayfa yukarı zıplamaz).
// Üstteki başka bir kart kapanıp yükseklik değişse bile tıklanan kartın başlığı
// görünümde aynı noktada kalır.
export function toggleWithScrollPreserve(e, key, setOpenKey) {
  const btn = e.currentTarget;
  const prevTop = btn.getBoundingClientRect().top;
  setOpenKey((prev) => (prev === key ? null : key));
  requestAnimationFrame(() => {
    const delta = btn.getBoundingClientRect().top - prevTop;
    if (Math.abs(delta) > 1) window.scrollBy(0, delta);
  });
}


export function formatDateTR(iso) {
  if (!iso || iso.length < 10) return iso || "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  const mi = Number(m) - 1;
  return `${d} ${TR_MONTHS[mi] || m} ${y}`;
}

export function thisMonthKey() {
  return new Date().toISOString().slice(0, 7); // YYYY-MM
}

// Kilitli/otomatik "İşlemi Yapan" alanı. Yönetici (owner/manager) kilidi açıp
// başka biri adına kayıt girebilir; diğer roller için her zaman kilitli.
export function PerformerField({ label, user, value, onChange, testId }) {
  const isManager = user?.role === "owner" || user?.role === "manager";
  const [override, setOverride] = useState(false);
  const locked = !override;

  const toggle = () => {
    if (override) { setOverride(false); onChange(""); }
    else { setOverride(true); onChange(user?.name || ""); }
  };

  return (
    <Field label={
      <span className="inline-flex items-center gap-1">
        {label}
        {locked
          ? <Lock size={11} weight="fill" className="text-[#8a938c]" />
          : <PencilSimple size={11} weight="bold" className="text-[#8B6914]" />}
      </span>
    }>
      {locked ? (
        <Input data-testid={testId} readOnly value={user?.name || ""}
               className="bg-[#EAF1EC] text-[#2D4A36] font-semibold cursor-not-allowed border-[#c9d5cc]"
               title="Oturum açan kullanıcı — otomatik doldurulur" />
      ) : (
        <Input data-testid={testId} value={value || ""} autoFocus
               onChange={e => onChange(e.target.value)}
               placeholder="İşlemi yapan kişinin adı" />
      )}
      {isManager && (
        <button type="button" data-testid={`${testId}-toggle`} onClick={toggle}
                className="text-[11px] font-medium text-[#8B6914] hover:underline mt-1">
          {override ? "↩ Otomatik (kendi adım) kullan" : "✎ Başka biri adına gir"}
        </button>
      )}
    </Field>
  );
}

export function MonthlySummaryBar({ stats, testId }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2.5 bg-[#F7F6F3] border-b border-[#ECE8E0]"
         data-testid={testId}>
      <span className="text-[10px] uppercase tracking-widest text-[#8a938c] font-semibold">Bu Ay Özeti</span>
      {stats.map((s, i) => (
        <span key={s.label} className="text-xs text-[#5e675e]">
          {s.label}: <b className={`tabnum ${s.tone || "text-[#2D4A36]"}`}>{s.value}</b>
        </span>
      ))}
    </div>
  );
}

function PaginationBar({ page, totalPages, total, visibleCount, onPage, testId }) {
  const btn = "px-2.5 py-1 rounded-md text-xs font-semibold border border-[#E5E2DC] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F0EEE8]";
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#ECE8E0]" data-testid={`${testId}-pagination`}>
      <span className="text-xs text-[#8a938c]">Toplam <b className="tabnum text-[#5e675e]">{total}</b> kayıt · Bu sayfa <b className="tabnum text-[#5e675e]">{visibleCount}</b> · Sayfa {page}/{totalPages}</span>
      <div className="flex gap-1.5">
        <button data-testid={`${testId}-prev`} className={btn} disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <CaretLeft size={12} weight="bold" /> Önceki
        </button>
        <button data-testid={`${testId}-next`} className={btn} disabled={page >= totalPages} onClick={() => onPage(page + 1)}>
          Sonraki <CaretRight size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}

// Tarihe göre gruplanmış, sayfalanmış tablo. columns: [{header, cell, thClass, tdClass}]
export function GroupedTable({ items, columns, dayMeta, summary, emptyText, testId, className = "", pageSize = 10, renderExpanded }) {
  const [page, setPage] = useState(1);
  const total = items.length;

  // Önce TÜM kayıtları güne göre grupla (aynı tarihleri birleştir, sırayı koru)
  const groupMap = new Map();
  for (const it of items) {
    const d = (it.date || "—").slice(0, 10);
    if (!groupMap.has(d)) groupMap.set(d, []);
    groupMap.get(d).push(it);
  }
  const allGroups = [...groupMap.entries()].map(([date, entries]) => ({ date, entries }));

  // Sayfalara böl: bir gün asla iki sayfaya bölünmez; sayfa başına ~pageSize kayıt
  const pages = [];
  let curGroups = [];
  let curCount = 0;
  for (const g of allGroups) {
    if (curCount > 0 && curCount + g.entries.length > pageSize) {
      pages.push(curGroups); curGroups = []; curCount = 0;
    }
    curGroups.push(g); curCount += g.entries.length;
  }
  if (curGroups.length) pages.push(curGroups);

  const totalPages = Math.max(1, pages.length);
  const cur = Math.min(page, totalPages);
  const groups = pages[cur - 1] || [];

  return (
    <Card className={`!p-0 overflow-hidden ${className}`} testId={testId}>
      {summary}
      <div className="overflow-x-auto">
        <table className="data-table">
          <thead><tr>{columns.map((c, i) => <th key={`${c.header}-${i}`} className={c.thClass}>{c.header}</th>)}</tr></thead>
          <tbody>
            {groups.map(g => (
              <React.Fragment key={g.date}>
                <tr className="bg-[#F1EFE8]" data-testid={`${testId}-daygroup-${g.date}`}>
                  <td colSpan={columns.length} className="!py-1.5">
                    <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#2D4A36]">
                      <CalendarBlank size={13} weight="duotone" /> {formatDateTR(g.date)}
                      {dayMeta && <span className="text-[#8a938c] font-normal">· {dayMeta(g.entries)}</span>}
                      <span className="text-[#b3b0a6] font-normal">({g.entries.length} kayıt)</span>
                    </span>
                  </td>
                </tr>
                {g.entries.map((it, idx) => {
                  const expanded = renderExpanded ? renderExpanded(it) : null;
                  return (
                    <React.Fragment key={it.id || idx}>
                      <tr data-testid={`${testId}-row-${it.id}`}>
                        {columns.map((c, i) => <td key={`${c.header}-${i}`} className={c.tdClass}>{c.cell(it)}</td>)}
                      </tr>
                      {expanded && (
                        <tr className="bg-[#F5F2EC]"><td colSpan={columns.length} className="p-3">{expanded}</td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
            {!total && <tr><td colSpan={columns.length} className="text-center py-8 text-[#8a938c]">{emptyText}</td></tr>}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && <PaginationBar page={cur} totalPages={totalPages} total={total} visibleCount={groups.reduce((a, g) => a + g.entries.length, 0)} onPage={setPage} testId={testId} />}
    </Card>
  );
}
